import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { LayoutDashboard, Map, Package, Mail, Plus, Home as HomeIcon, Plane, Building2, Wallet } from "lucide-react";
import { useState } from "react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Menu } from "lucide-react";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/itineraries", label: "Itineraries", icon: Map },
  { to: "/admin/packages", label: "Tour Packages", icon: Package },
  { to: "/admin/hotels", label: "Hotels", icon: Building2 },
  { to: "/admin/accounting", label: "Accounting", icon: Wallet },
  { to: "/admin/inquiries", label: "Inquiries", icon: Mail },
];

export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const isEditor = loc.pathname !== '/admin/itineraries' && loc.pathname.startsWith('/admin/itineraries/');

  const [isAuth, setIsAuth] = useState(localStorage.getItem("hp_admin_auth") === "true");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handleLogin = (e) => {
    e.preventDefault();
    if (username === "krishna" && password === "Admin#@123") {
      setIsAuth(true);
      localStorage.setItem("hp_admin_auth", "true");
    } else {
      setError("Invalid username or password");
    }
  };

  if (!isAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center admin-bg p-4">
        <div className="glass-panel-dark p-8 rounded-2xl shadow-2xl max-w-sm w-full">
          <div className="text-center mb-6">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#1D4ED8] to-[#0EA5E9] flex items-center justify-center mx-auto mb-3 shadow-[0_0_20px_rgba(29,78,216,0.5)]">
              <Plane className="w-6 h-6 text-slate-800" />
            </div>
            <h1 className="font-display font-bold text-2xl text-slate-800">Admin Login</h1>
            <p className="text-sm text-slate-500 mt-1">Sign in to manage Happy Journey 4 U</p>
          </div>
          <form onSubmit={handleLogin} className="flex flex-col gap-4">
            {error && <div className="text-red-500 text-sm text-center bg-red-50 p-2 rounded-lg">{error}</div>}
            <input
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-2 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#1D4ED8] text-slate-800 placeholder-slate-400 text-sm transition-all"
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#1D4ED8] text-slate-800 placeholder-slate-400 text-sm transition-all"
              required
            />
            <Button type="submit" className="w-full rounded-xl btn-luxe ">
              Login
            </Button>
          </form>
        </div>
      </div>
    );
  }

  const Nav = ({ onClick }) => (
    <nav className="flex flex-col gap-1 p-3">
      <div className="px-2 py-2 flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#ea580c] to-[#f97316] flex items-center justify-center shadow-[0_0_15px_rgba(234,88,12,0.4)]">
          <Plane className="w-4 h-4 text-white" />
        </div>
        <div className="leading-tight">
          <div className="font-display font-bold text-slate-800 text-sm">Happy Journey 4 U</div>
          <div className="text-[10px] text-slate-500 -mt-0.5">Admin</div>
        </div>
      </div>
      <Link to="/admin/itineraries/new" onClick={onClick}>
        <Button className="w-full mt-2 mb-3 rounded-xl btn-luxe " data-testid="sidebar-new-itinerary-button">
          <Plus className="w-4 h-4 mr-2" /> New Itinerary
        </Button>
      </Link>
      {LINKS.map((l) => (
        <NavLink
          key={l.to}
          to={l.to}
          end={l.end}
          onClick={onClick}
          data-testid={`admin-nav-${l.label.toLowerCase().replace(/ /g, '-')}-link`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
              isActive ? "bg-slate-100 text-slate-800 shadow-sm border border-slate-200 font-semibold" : "text-slate-600 hover:bg-slate-50 hover:text-slate-800"
            }`
          }
        >
          {({ isActive }) => (
            <>
              <l.icon className={`w-4 h-4 ${isActive ? 'text-orange-500 drop-shadow-[0_0_8px_rgba(96,165,250,0.5)]' : ''}`} />
              {l.label}
            </>
          )}
        </NavLink>
      ))}
      <div className="mt-auto pt-4 border-t border-slate-200 mt-6">
        <Link to="/" onClick={onClick} className="flex items-center gap-2 px-3 py-2 text-xs text-slate-500 hover:text-slate-800 transition-colors">
          <HomeIcon className="w-4 h-4" /> View Website
        </Link>
      </div>
    </nav>
  );

  return (
    <div className="min-h-screen flex admin-bg">
      {/* Desktop sidebar */}
      {!isEditor && (
        <aside className="hidden lg:flex flex-col w-64 shrink-0 glass-panel-dark border border-slate-200 m-4 mr-0 rounded-2xl overflow-y-auto" data-testid="admin-sidebar">
          <Nav />
        </aside>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="glass-nav-dark sticky top-0 lg:top-4 z-40 h-14 flex items-center px-4 lg:px-6 m-4 lg:ml-4 lg:mb-0 rounded-2xl border border-slate-200">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className={isEditor ? "block mr-3 text-slate-800 hover:bg-transparent hover:text-[#ea580c] focus:ring-0 focus:outline-none" : "lg:hidden mr-3 text-slate-800 hover:bg-transparent hover:text-[#ea580c] focus:ring-0 focus:outline-none"} data-testid="admin-sidebar-toggle">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72 bg-[#faf9f6] border-r border-slate-200">
              <Nav onClick={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
          <div className="font-display font-semibold text-slate-800">Happy Journey 4 U · Admin</div>
          <div className="ml-auto flex items-center gap-3">
            <div className="hidden sm:block text-xs text-slate-500">Logged in as <span className="text-orange-500 font-medium drop-shadow-[0_0_5px_rgba(96,165,250,0.5)]">krishna</span></div>
            <Button variant="outline" size="sm" className="h-8 text-xs rounded-lg border-white/20 text-slate-600 hover:bg-slate-100 hover:text-white bg-transparent" onClick={() => { localStorage.removeItem("hp_admin_auth"); setIsAuth(false); }}>Logout</Button>
          </div>
        </header>

        <main key={loc.pathname} className="flex-1 flex flex-col p-4 lg:px-8 lg:py-6 pb-20 lg:pb-8">
          <Outlet />
        </main>
      </div>

      {/* Mobile Bottom Navigation (only for non-editor pages) */}
      {!isEditor && (
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 glass-nav-dark border-t border-slate-200 z-50 pb-safe">
          <div className="flex justify-around items-center h-16">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center w-full h-full space-y-1 transition-colors ${
                    isActive ? "text-orange-500" : "text-slate-500 hover:text-slate-800"
                  }`
                }
              >
                <l.icon className="w-5 h-5" />
                <span className="text-[10px] font-medium">{l.label.split(' ')[0]}</span>
              </NavLink>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}
